import CheckIcon from "@mui/icons-material/Check";
import { Box, buttonClasses, Typography, useTheme, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import { darken, styled } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React from "react";
import dayjs from "dayjs";

import ArrowDownIcon from "../../../icons/CaretDownIcon";
import CalendarIcon from "../../../icons/CalendarIcon";
import HyroDatePicker from "../../hyro-components/hyro-date-picker/HyroDatePicker";
import { FROM_DATE_LIMIT, FROM_TO_KEYWORDS } from "./DatePicker.consts";
import HyroTimePicker from "../../hyro-components/hyro-time-picker/HyroTimePicker";
import { TIME_PICKER_MODES, TIME_PICKER_STYLE } from "../../hyro-components/hyro-time-picker/HyroTimePicker.consts";
import usePredefinedDatePicker from "./DatePickerWithPresets.hook";
import { setTimeOfDate } from "./DatePicker.utils";
import useAppStore from "../../../stores/appStore";
import { selectedAssistantSelector } from "../../../stores/selectors/appSelectors";
import { DEFAULT_TIMEZONE, TIMEZONE_DISPLAY_NAMES } from "@consts";

const setSecondaryBtnColor = theme => ({
  backgroundColor: theme.palette.tertiary[300],
  padding: `${theme.spacing(1)} 12px`
});

const PredefinedButton = styled(Button)(({ theme }) => ({
  ...setSecondaryBtnColor(theme),
  height: 40,
  minWidth: "fit-content",
  justifyContent: "flex-start",
  "&:hover": {
    backgroundColor: "#E0E0FF"
  },
  [`.${buttonClasses.startIcon}`]: {
    fontSize: 18
  },
  [`.${buttonClasses.endIcon} > *:nth-of-type(1)`]: {
    fontSize: 20
  },
  [`.${buttonClasses.endIcon}`]: {
    marginLeft: "4px"
  }
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0, 0, 0.5, 0),
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== "selected"
})(({ selected, theme }) => ({
  "padding": theme.spacing(1, 2),
  "borderRadius": theme.shape.borderRadius,
  "&:hover": {
    backgroundColor: darken(theme.palette.background.default, 0.04)
  },
  ...(selected && {
    color: theme.palette.primary[500],
    backgroundColor: theme.palette.tertiary[300]
  })
}));

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: prop => prop !== "selected"
})(({ theme, selected }) => ({
  color: selected ? theme.palette.primary[500] : theme.palette.text[700],
  svg: {
    display: "none",
    verticalAlign: "top",
    marginLeft: "auto",
    ...(selected && { display: "block" })
  }
}));

const PredefinedDatePicker = (
  {
    dates,
    setDates,
    minDate,
    disabled,
    fromDateLimit = FROM_DATE_LIMIT.THREE_MONTHS,
    allowSearchParams,
    fromKeyword = FROM_TO_KEYWORDS.START_DATE,
    toKeyword = FROM_TO_KEYWORDS.END_DATE,
    testId = "",
    showStandaloneDatePicker,
    presetOptions,
    hideTimePickers,
    customRangeLabelFormat,
    useUtc
  }
) => {
  const theme = useTheme();
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const timezoneKey = selectedAssistant?.timezone || DEFAULT_TIMEZONE;
  const timezoneInfo = TIMEZONE_DISPLAY_NAMES[timezoneKey];
  const timezoneDisplayName = timezoneInfo?.long;

  const {
    dateOptions,
    selectedDate,
    localDates,
    isMenuOpen,
    menuAnchorEl,
    isMenuItemSelected,
    handleOpenMenu,
    handleCloseMenu,
    selectDate,
    shouldDisableFromDate,
    shouldDisableToDate,
    isRangeInvalid
  } = usePredefinedDatePicker(dates, setDates, fromDateLimit, allowSearchParams, fromKeyword, toKeyword, testId, presetOptions, minDate);

  const labelDates = isRangeInvalid ? dates : localDates;
  const triggerLabel = selectedDate
    ? selectedDate.label
    : customRangeLabelFormat && labelDates?.[0] && labelDates?.[1]
      ? `${labelDates[0].format(customRangeLabelFormat)} - ${labelDates[1].format(customRangeLabelFormat)}`
      : "Custom";

  const datePickerElement = <Stack
    direction="column"
    sx={{
      padding: theme => `0 ${theme.spacing(2)}`
    }}
  >
    <Typography variant="body2" color="text.700">Date{useUtc ? " (UTC Time)" : ""}</Typography>
    <ListItem data-testid="select-dates" sx={{ gap: theme => theme.spacing(1.5), flexDirection: "row", padding: theme => `${theme.spacing(1)} 0` }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <HyroDatePicker
          testId="start-date-picker"
          value={localDates ? localDates[0] : null}
          minDate={minDate}
          shouldDisableDate={shouldDisableFromDate}
          invalidRange={isRangeInvalid}
          onChange={value => {
            if (!value) return;
            const currentStartTime = localDates[0]?.format("HH:mm");
            const newStartDate = setTimeOfDate(value, currentStartTime);
            selectDate({ startDate: newStartDate, endDate: localDates[1] }, false, !dayjs(value).isValid());
          }}
        />
        <Box sx={{ width: "8px", height: "1.2px", backgroundColor: theme => theme.palette.text[700] }} />
        <HyroDatePicker
          value={localDates ? localDates[1] : null}
          minDate={minDate}
          shouldDisableDate={shouldDisableToDate}
          invalidRange={isRangeInvalid}
          onChange={value => {
            if (!value) return;
            const currentEndTime = localDates[1]?.format("HH:mm");
            const newEndDate = setTimeOfDate(value, currentEndTime);
            selectDate({ startDate: localDates[0], endDate: newEndDate }, false, !dayjs(value).isValid());
          }}
          testId="end-date-picker"
        />
      </LocalizationProvider>
    </ListItem>
    {!hideTimePickers && <>
      <Typography variant="body2" color="text.700" sx={{ paddingTop: theme => `${theme.spacing(1)}` }}>Time ({timezoneDisplayName})</Typography>
      <ListItem sx={{ padding: theme => `${theme.spacing(1)} 0` }}>
        <HyroTimePicker
          error={localDates[0]?.isSame(localDates[1], "day") && localDates[0]?.format("HH:mm") > localDates[1]?.format("HH:mm")}
          mode={TIME_PICKER_MODES.RANGE}
          isRangePersistent={false}
          layoutStyle={TIME_PICKER_STYLE.WIDE}
          value={{
            startTime: localDates[0]?.format("HH:mm"),
            endTime: localDates[1]?.format("HH:mm")
          }}
          onChange={({ startTime, endTime }) => {
            const startDate = setTimeOfDate(localDates[0], startTime);
            const endDate = setTimeOfDate(localDates[1], endTime);
            selectDate({ startDate, endDate }, false, false);
          }}
          fromKeyword="startTime"
          toKeyword="endTime"
          toPlaceholder="12:00 am"
          fromPlaceholder="11:59 pm"
        />
      </ListItem>
    </>}
  </Stack>;

  if (showStandaloneDatePicker) {
    return datePickerElement;
  }

  return (
    <>
      <PredefinedButton
        data-testid={`${testId}_button`}
        disabled={disabled}
        onClick={handleOpenMenu}
        size="small"
        aria-describedby="thisButton"
        startIcon={<CalendarIcon color={disabled ? theme.palette.system[400] : theme.palette.primary[500]} />}
        endIcon={<ArrowDownIcon color={disabled ? theme.palette.system[400] : theme.palette.primary[500]} />}
      >
        <Typography data-testid='selected-date' variant={"buttonMedium"}>{triggerLabel}</Typography>
      </PredefinedButton>
      <Menu
        data-testid={`${testId}_menu_button`}
        id="thisButton"
        open={isMenuOpen}
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        PaperProps={{
          style: {
            width: 400,
            padding: "8px 8px 16px 8px",
          }
        }}
      >
        {dateOptions.map(dateOption => (
          <StyledListItem key={dateOption.key}>
            <StyledListItemButton
              {...{
                onClick: () => {
                  selectDate(dateOption, true);
                },
                selected: isMenuItemSelected(dateOption)
              }}
            >
              <ListItemText>
                <Typography data-testid={dateOption.testId} variant="body2">{dateOption.label}</Typography>
              </ListItemText>
              <StyledListItemIcon
                {...{ selected: isMenuItemSelected(dateOption) }}
              >
                <CheckIcon />
              </StyledListItemIcon>
            </StyledListItemButton>
          </StyledListItem>
        ))}
        <Divider sx={{ margin: theme => theme.spacing(1, 0, 1.5, 0) }} />
        {datePickerElement}
      </Menu>
    </>
  );
};

export default PredefinedDatePicker;
